import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { Helper } from 'src/app/_classes';
import { ScheduledEntry } from 'src/app/_interfaces';
import { isSameDay, localizeDate } from 'src/app/_functions';
import { AppStringsService } from 'src/app/_services';
import { MatIcon } from '@angular/material/icon';
import { SsrlocalstoreService } from 'src/app/_services/ssr-localstore.service';

@Component({
    selector: 'app-scheduled-entry',
    templateUrl: './scheduled-entry.component.html',
    styleUrls: ['./scheduled-entry.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatIcon]
})
export class ScheduledEntryComponent implements OnInit {
    @Input() entry: ScheduledEntry = {} as ScheduledEntry;
    @Input() format: 'standard' | 'square' | 'search_list' = 'standard';
    @Input() margin_bottom: string = '1em';
    @Input() showTourTypes: boolean = false;

    public formatter: Intl.DateTimeFormat = {} as Intl.DateTimeFormat;

    constructor(
        public appStringAPI: AppStringsService,
        private ssr: SsrlocalstoreService
    ) {}

    ngOnInit(): void {
        this.formatter = new Intl.DateTimeFormat(this.getpreferredLanguage(), {
            timeZone: 'GMT',
            day: 'numeric',
            month: 'numeric',
            year: '2-digit'
        });
    }

    // ngAfterViewInit(): void {
    //     setTimeout(() => {
    //         requestAnimationFrame(() => this.refitAll());
    //         requestAnimationFrame(() => this.refitAll());

    //         window.addEventListener('load', this.refitAll, { once: true });
    //     }, 500);
    // }

    // onCardAssetLoaded = () => this.refitAll();

    // private refitAll = () => {
    //     if (!this.fitters) return;
    //     this.fitters.forEach(d => d.refit());
    // };

    isSameDay = isSameDay;

    handleRatio(entry: ScheduledEntry): string {
        return entry.partnership_level === 'standard' ? '4.5 / 1' : '3 / 1';
    }

    handleBackground(url: string): string {
        return new Helper().handleBackgroundURL(url);
    }

    localizeDate = (date: string) => this.formatter.format(localizeDate(date));

    get purseText(): string | null {
        const purse = this.normalizedValue(this.entry?.purse);
        return purse ? `${this.appStringAPI.getAppString('golf.purse')} ${purse}` : null;
    }

    get winnerText(): string | null {
        const winnersName = this.normalizedValue(this.entry?.winners_name);
        const winnersScore = this.normalizedValue(this.entry?.winners_score);
        const isPastEntry = this.entry?.timeframe === 'past';

        if (isPastEntry && winnersName) {
            const winnerScoreSuffix = winnersScore ? ` (${winnersScore})` : '';
            return `${this.appStringAPI.getAppString('golf.winner')}: ${winnersName}${winnerScoreSuffix}`;
        }

        return null;
    }

    get squareMetaText(): string | null {
        return this.winnerText ?? this.purseText;
    }

    getpreferredLanguage(): string {
        return (this.ssr?.getItem?.('preferredLanguage') as string) || 'en';
    }

    statusString(item: ScheduledEntry): 'golf.tournament.live' | 'golf.tournament.suspended' {
        return item.is_suspended ? 'golf.tournament.suspended' : 'golf.tournament.live';
    }

    showStatus(item: ScheduledEntry): boolean {
        return item.is_live || item.is_suspended;
    }

    get tourTypesLabel(): string {
        if (!this.showTourTypes) {
            return '';
        }

        const rawTypes = this.entry?.theme?.types;

        if (!Array.isArray(rawTypes) || !rawTypes.length) {
            return '';
        }

        const labels = rawTypes
            .map((type) => this.mapTourTypeToAppString(type))
            .filter((key): key is string => !!key)
            .map((key) => this.appStringAPI.getAppString(key))
            .filter(Boolean);

        return labels.join(', ');
    }

    hasTourTypes(): boolean {
        return !!this.tourTypesLabel;
    }

    private normalizedValue(value?: string | null): string | null {
        const normalized = String(value ?? '').trim();
        return normalized ? normalized : null;
    }

    private mapTourTypeToAppString(type: string): string | null {
        if (!type) {
            return null;
        }

        const normalized = String(type)
            .trim()
            .replace(/([a-z])([A-Z])/g, '$1_$2')
            .replace(/[\s-]+/g, '_')
            .toLowerCase();

        const map: Record<string, string> = {
            pga: 'golfscores.filter.pga',
            lpga: 'golfscores.filter.lpga',
            european_tour: 'golfscores.filter.european_tour',
            ladies_european_tour: 'golfscores.filter.ladies_european',
            ladies_european: 'golfscores.filter.ladies_european',
            korn_ferry: 'golfscores.filter.korn_ferry',
            liv_golf: 'golfscores.filter.liv',
            liv: 'golfscores.filter.liv',
            asian_tour: 'golfscores.filter.asian_tour',
            challenge_tour: 'golfscores.filter.challenge_tour',
            pga_tour_champions: 'golfscores.filter.pga.champions',
            pga_champions: 'golfscores.filter.pga.champions',
            sunshine_tour: 'golfscores.filter.sunshine_tour'
        };

        return map[normalized] ?? null;
    }
}
